import SearchBar from '@src/components/SearchBar';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import ToolCard from './Componenets/ToolCard';
import TallyImage from '@src/assets/images/tally.svg';
import SapImage from '@src/assets/images/sap.svg';
import OracleImage from '@src/assets/images/oracle.svg';
import { ApplicationContext } from '@src/contexts/ApplicationContext';
import { AxiosError, AxiosResponse } from 'axios';
import Loading from '@src/components/Loading/Loading';
import ManageTally from './ManageTally';
import { IntegrationsView } from '..';
import { CustomAlertType } from '@src/interfaces/type';
import DataNotFound from '@src/components/DataItemFound/DataNotFound';
import moment from 'moment';
import SecurityAlertModal from '@src/components/ConfirmModal/SecurityAlertModal';
import CustomModal from '@src/components/CustomModal';
import { InfoCircleIcon, LockIcon, NoTools } from '@src/components/Icons';
import { useAppSelector } from '@src/store/hooks';
import { adminUsers } from '@src/helpers/Permission';
import CustomPermission from '@src/components/Permissions/CustomPermission';
import ComingSoonModal from '@src/components/CustomModal/ComingSoonModal';
import HighlightedInfo from '@src/components/CustomInfoMessages/HighlightedInfo';
import ErpModal from './Componenets/ErpModal';
import { RESTAURANT } from '@src/Constants';
import { IsMobileWidth } from '@src/components/MediaQueryUtils';

const StyledContainer = styled.div`
	display: flex;
	flex-direction: column;
	max-width: 100%;
	height: 100%;
	background-color: var(--chat-header-bg);
	overflow: scroll;
	flex-grow: 1;
	padding: 24px;
	gap: 24px;
	@media screen and (max-width: 600px) {
		height: calc(100vh - 142px);
	}
	.container1 {
		display: flex;
		justify-content: space-between;
		align-items: center;
		p {
			color: var(--color-font-grey);
			font-size: 16px;
			font-weight: 600;
		}
		@media screen and (max-width: 600px) {
			gap: 15px;
			flex-direction: column;
			p {
				font-size: 12px;
			}
		}
	}
`;

export interface ToolFormat {
	id?: string;
	name?: string;
	description?: string;
	image?: any;
	enable?: boolean;
	status?: string | null;
	last_activity?: string | null;
	loading?: boolean;
	site?: {
		href: string;
		text: string;
	};
	switcherDisable?: boolean;
	userName: string;
	password: string;
	onClick?: any;
	handleSwitchToggle?: any;
	tallyIntegrated: boolean;
}

interface IntegrationProps {
	view: IntegrationsView;
	setView: (data: IntegrationsView) => void;
	setAlert: (data: CustomAlertType) => void;
	displayCustomError: any;
}

const Integrations: React.FC<IntegrationProps> = ({
	view,
	setView,
	setAlert,
	displayCustomError,
}) => {
	const { apiService }: any = React.useContext(ApplicationContext);

	const currentUser: any = useAppSelector((state) => state?.currentUser?.value);
	const isAdmin = adminUsers?.includes(currentUser?.userType);
	const isSupplier = currentUser?.type?.toLowerCase() !== RESTAURANT;

	const [searchValue, setSearchValue] = useState<string>('');
	const [loadingData, setLoadinData] = useState(true);
	const [apiCall, setApiCall] = useState(false);
	const [confirmDialog, setConfirmDialog] = useState(false);
	const [comingSoonModal, setComingSoonModal] = useState(false);
	const [erpModal, setErpModal] = useState(false);
	const [tool, setTool] = useState<ToolFormat | null>(null);
	const [tallyData, setTallyData] = useState<ToolFormat>({
		userName: '',
		password: '',
		tallyIntegrated: false,
		status: null,
		last_activity: null,
	});
	const toolsData = [
		{
			id: 'tally',
			name: 'Tally',
			description:
				'Automates data transfer, enhance accuracy and efficiency in financial processes.',
			image: {
				url: TallyImage,
				width: '43px',
				height: '21px',
			},
			enable: true,
			site: {
				text: 'tallysolutions.com',
				href: 'https://tallysolutions.com/',
			},
			switcherDisable: isSupplier ? false : true,
			userName: '',
			password: '',
			status: null,
			loading: false,
			tallyIntegrated: false,
			onClick: (data: ToolFormat) => {
				if (isSupplier) {
					setTallyData(data);
					setView('manageTally');
				}
			},
			handleSwitchToggle: (data: ToolFormat) => {
				setTool(data);
				setConfirmDialog(true);
			},
		},
		{
			id: 'SAP Business One',
			name: 'SAP Business One',
			description: 'Discover a single, affordable ERP solution for managing your entire company.',
			image: {
				url: SapImage,
				width: '32px',
				height: '32px',
			},
			enable: true,
			site: {
				text: 'www.sap.com/products/erp.html',
				href: '',
			},
			switcherDisable: true,
			userName: '',
			password: '',
			status: 'Offline',
			loading: false,
			tallyIntegrated: false,
			onClick: (data: ToolFormat) => {
				setComingSoonModal(true);
			},
			handleSwitchToggle: (data: ToolFormat) => {},
		},
		{
			id: 'ORACLE ERP',
			name: 'ORACLE ERP',
			description: 'Oracle Fusion Cloud ERP is a complete, modern, cloud ERP suite.',
			image: {
				url: OracleImage,
				width: '43.5px',
				height: '20px',
			},
			enable: true,
			site: {
				text: 'www.oracle.com/in/erp/',
				href: '',
			},
			switcherDisable: true,
			userName: '',
			password: '',
			status: 'Offline',
			loading: false,
			tallyIntegrated: false,
			onClick: (data: ToolFormat) => {
				setComingSoonModal(true);
			},
			handleSwitchToggle: (data: ToolFormat) => {},
		},
	];
	const [tools, setTools] = useState<ToolFormat[]>(toolsData);
	const [filteredTools, setFilteredTools] = useState<ToolFormat[]>(toolsData);

	const isMobileWidth = IsMobileWidth();

	const updateLoadingStateOfTool = (data: ToolFormat, loading: boolean) => {
		if (tool) {
			setTool({ ...tool, loading: loading });
		}
		setTools((prevState: any) =>
			prevState?.map((tool: ToolFormat) => {
				if (tool?.id === data?.id) {
					return { ...tool, loading: loading };
				}
				return tool;
			})
		);
		setFilteredTools((prevState: any) =>
			prevState?.map((tool: ToolFormat) => {
				if (tool?.id === data?.id) {
					return { ...tool, loading: loading };
				}
				return tool;
			})
		);
	};

	const updateTallyStatus = (data: ToolFormat | null) => {
		if (data) {
			const tallyData = {
				tally: !data?.enable,
			};
			updateLoadingStateOfTool(data, true);
			apiService.updateTallyStatus(
				tallyData,
				(response: AxiosResponse) => {
					updateLoadingStateOfTool(data, false);
					setApiCall(true);
					setAlert({
						severity: 'success',
						message: 'Tally Status Updated Successfully',
					});
					setConfirmDialog(false);
				},
				(error: AxiosError) => {
					updateLoadingStateOfTool(data, false);
					displayCustomError('Something went wrong!');
				}
			);
		}
	};

	const handleSearch = (value: any) => {
		setSearchValue(value);
		const filteredItems: any = tools?.filter((tool: any) => {
			const lowerCaseValue = value?.toLowerCase();

			return (
				tool?.name?.toLowerCase()?.includes(lowerCaseValue) ||
				tool?.id?.toLowerCase()?.includes(lowerCaseValue)
			);
		});
		setFilteredTools(filteredItems);
	};

	useEffect(() => {
		if (isAdmin && isSupplier) {
			apiService.getIntegrations(
				(response: AxiosResponse) => {
					const data = response?.data?.data;

					//Case: tally not intergrated
					if (data?.tally === null) {
						setTools((prevState: ToolFormat[]) =>
							prevState?.map((tool: ToolFormat) => {
								if (tool?.id === 'tally') {
									return {
										...tool,
										enable: false,
										userName: '',
										switcherDisable: true,
										tallyIntegrated: false,
									};
								}
								return tool;
							})
						);
						setFilteredTools((prevState: ToolFormat[]) =>
							prevState?.map((tool: ToolFormat) => {
								if (tool?.id === 'tally') {
									return {
										...tool,
										enable: false,
										userName: '',
										switcherDisable: true,
										tallyIntegrated: false,
									};
								}
								return tool;
							})
						);
					}

					if (data?.tally) {
						const { is_active, user_name, status, last_activity } = data?.tally;
						setTools((prevState: ToolFormat[]) =>
							prevState?.map((tool: ToolFormat) => {
								if (tool?.id === 'tally') {
									return {
										...tool,
										status: status,
										last_activity: last_activity
											? moment(last_activity).format('MMM D, YYYY hh:mma')
											: null,
										enable: is_active,
										userName: user_name,
										tallyIntegrated: true,
										switcherDisable: false,
									};
								}
								return tool;
							})
						);
						setFilteredTools((prevState: ToolFormat[]) =>
							prevState?.map((tool: ToolFormat) => {
								if (tool?.id === 'tally') {
									return {
										...tool,
										status: status,
										last_activity: last_activity
											? moment(last_activity).format('MMM D, YYYY hh:mma')
											: null,
										enable: is_active,
										userName: user_name,
										tallyIntegrated: true,
										switcherDisable: false,
									};
								}
								return tool;
							})
						);
					}
					if (apiCall) {
						setApiCall(false);
					}
					setLoadinData(false);
				},
				(error: AxiosError) => {
					setLoadinData(false);
					displayCustomError('Something went wrong!');
				}
			);
		} else {
			setLoadinData(false);
		}
	}, [apiCall, isAdmin]);

	useEffect(() => {
		return () => {
			setView('list');
		};
	}, []);

	if (!isAdmin) {
		return (
			<CustomPermission
				icon={<LockIcon />}
				title="We are sorry..."
				paragraph={`You do not have permission to access integrations page. Contact 
	an administrator to get permission.`}
			/>
		);
	}

	if (loadingData) {
		return <Loading />;
	}

	if (view === 'list') {
		return (
			<>
				{tools?.length ? (
					<StyledContainer>
						<div className="container1">
							<p>
								Cheeni integrates easily with 3rd party software. Effortlessly sync your catalogs,
								automate receive invoices and send instant confirmations from Cheeni.
							</p>
							<SearchBar
								style={{
									width: '312px',
									height: '36px',
								}}
								value={searchValue}
								onChange={handleSearch}
								isLoading={false}
							/>
						</div>
						<HighlightedInfo>
							<span className="bold">Connect ERP ?</span>
							<p style={{ color: '#5C5C5C' }}>
								If you want to use a different software that’s not listed here, just{' '}
								<span
									className="link"
									onClick={() => {
										if (isSupplier) {
											setErpModal(true);
										}
									}}
								>
									let us know
								</span>{' '}
								and we can connect it.
							</p>
						</HighlightedInfo>
						<div>
							{filteredTools?.length ? (
								filteredTools?.map((tool: any) => {
									return (
										<ToolCard
											key={tool?.id}
											tool={tool}
											setTools={setTools}
											setFilteredTools={setFilteredTools}
										/>
									);
								})
							) : (
								<DataNotFound Image={NoTools} content="No Tools Found" />
							)}
						</div>
					</StyledContainer>
				) : (
					<DataNotFound Image={NoTools} content="No Tools Found" />
				)}
				{confirmDialog ? (
					<CustomModal
						open={confirmDialog}
						setOpen={setConfirmDialog}
						header={false}
						style={{ width: '357px', height: 'auto' }}
						children={
							<SecurityAlertModal
								handleClose={() => setConfirmDialog(false)}
								handleConfirm={() => updateTallyStatus(tool)}
								title="Are You Sure?"
								content="you want to change status of your tool integration."
								btnView="1"
								icon={
									<div
										style={{
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											width: '48px',
											height: '48px',
											background: '#FFF9E6',
											borderRadius: '50%',
										}}
									>
										<InfoCircleIcon fill="#FFBE00" width="24" height="24" />
									</div>
								}
								loading={tool?.loading || false}
							/>
						}
					/>
				) : null}
				{comingSoonModal ? (
					<ComingSoonModal
						style={isMobileWidth ? { width: '90%' } : {}}
						open={comingSoonModal}
						setOpen={setComingSoonModal}
					/>
				) : null}
				{erpModal ? (
					<ErpModal
						open={true}
						setOpen={setErpModal}
						setAlert={setAlert}
						displayCustomError={displayCustomError}
					/>
				) : null}
			</>
		);
	} else if (view === 'manageTally') {
		return (
			<ManageTally
				data={tallyData}
				setData={setTallyData}
				setApiCall={setApiCall}
				setAlert={setAlert}
				displayCustomError={displayCustomError}
			/>
		);
	}
	return null;
};

export default Integrations;
